import React from 'react';
import PropTypes from 'prop-types';
import uniqid from 'uniqid';
import _ from 'lodash';
import Case from 'case';
import {
  Box, Text, Anchor, Button,
} from 'grommet';
import {
  Spinning, SimpleModal, DropdownSort,
} from '@Components/Control';
import { useAppTheme } from '@Components/Context';
import { ReviewTablePartial } from '@Components/Partial/Product/Dashboard';
import { isValidUrl, buildDateTimeLabel } from '@Helpers/util';
import {
  SearchInput, FilterDropdown,
} from '../SharedComponents';
import {
  StyledText, StyledRaisedContainer,
} from '../StyledSharedComponents';
import { StyledStatusBadge } from '../ListSearches/StyledListSearches';

export const SearchDetails = ({
  small,
  details,
  textColor,
  highlightText,
  statusColorMap = null,
  weight = 'normal',
  attributes = false,
}) => {
  const [showModal, setShowModal] = React.useState(false);
  const [showLinksModal, setShowLinksModal] = React.useState(null);
  const { theme: { page } } = useAppTheme();

  const selectStatusColor = (status, type) => {
    const currentStatus = statusColorMap[status];

    if (currentStatus) return currentStatus[type];

    return null;
  };

  const getLinkLabel = (link) => {
    const parts = link.split('/').filter(Boolean);
    return parts[parts.length - 1] || link;
  };

  const renderValue = (val, type, isStatus = false) => {
    if (Array.isArray(val) && type === 'links') {
      const visibleLinks = val.slice(0, 3);
      return (
        <Box direction="row" wrap gap="0.25rem">
          {visibleLinks.map((link, i) => (
            <React.Fragment key={uniqid()}>
              <Anchor href={link} target="_blank" size="0.9rem">
                <Text color={highlightText} size="0.8rem">{getLinkLabel(link)}</Text>
              </Anchor>
              {i < visibleLinks.length - 1 && <Text size="0.8rem" color={textColor}>,</Text>}
            </React.Fragment>
          ))}
        </Box>
      );
    }

    if (isValidUrl(val) && type !== 'list') {
      return (
        <Anchor href={val} target="_blank">
          <Text color={textColor} size="1rem" weight={weight || 'normal'}>{val}</Text>
        </Anchor>
      );
    }

    if (isStatus) {
      return (
        <StyledStatusBadge background={selectStatusColor(Case.lower(val), 'background')}>
          <Text size="1rem" color={selectStatusColor(Case.lower(val), 'text')} weight={600}>
            {val}
          </Text>
        </StyledStatusBadge>
      );
    }

    return (
      <Text color={textColor} size="1rem" weight="normal">{val}</Text>
    );
  };

  const renderDetails = (label, formatValue = null, value, type, unit) => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return null;
    }

    let displayValue = type === 'list' ? value.join(', ') : value;
    let restValues = null;

    if (type === 'list' && value.length > 3) {
      let firstValues = value.slice(0, 3);
      restValues = value.slice(3);

      if (small) {
        firstValues = value.slice(0, 2);
        restValues = value.slice(2);
      }

      displayValue = `${firstValues.join(', ')} + ${restValues.length} more`;
    }

    if ((type === 'date' || type === 'datetime') && formatValue) {
      const formattedValue = buildDateTimeLabel(formatValue);

      displayValue = `${value} (${formattedValue})`;
    }

    if (unit) {
      displayValue = `${value}${unit}`;
    }

    return (
      <Box
        key={uniqid()}
        direction="row"
        gap="0.5rem"
        width="100%"
        justify="start"
        align="center"
      >
        <Text color={textColor} size="1rem" weight={weight || 'normal'}>{`${label}:`}</Text>
        <Box direction="row" gap="0.5rem" align="center">
          {renderValue(displayValue, type, label === 'Status')}
          {restValues?.length >= 1 && (
            <>
              <Box width="fit-content" style={{ display: 'inline-block' }}>
                <Button plain onClick={() => setShowModal(true)}>
                  <Text size="0.9rem" weight={500} color={page.highlight}>
                    (View all)
                  </Text>
                </Button>
              </Box>
              {showModal && (
                <SimpleModal
                  small={small}
                  toggleModal={setShowModal}
                  modalTitle={label}
                  closeLabel="Close"
                  highlightColor={page.highlight}
                  titleSize="1.2rem"
                >
                  <Box pad={{ vertical: '0.5rem' }}>
                    <Text color="var(--page-modal-text)" size="1rem" style={{ lineHeight: 1.5 }}>{value.join(', ')}</Text>
                  </Box>
                </SimpleModal>
              )}
            </>
          )}
          {type === 'links' && Array.isArray(value) && value.length > 3 && (
            <>
              <Box width="fit-content" style={{ display: 'inline-block' }}>
                <Button plain onClick={() => setShowLinksModal(label)}>
                  <Text size="0.9rem" weight={500} color={page.highlight}>
                    {`+${value.length - 3} more`}
                  </Text>
                </Button>
              </Box>
              {showLinksModal === label && (
                <SimpleModal
                  small={small}
                  toggleModal={() => setShowLinksModal(null)}
                  modalTitle={label}
                  closeLabel="Close"
                  textColor={textColor}
                  highlightColor={page.highlight}
                  modalBg="white"
                  titleSize="1.2rem"
                >
                  <Box pad={{ vertical: '0.5rem' }} gap="0.5rem">
                    {value.map((link) => (
                      <Anchor key={uniqid()} href={link} target="_blank" size="0.9rem">{getLinkLabel(link)}</Anchor>
                    ))}
                  </Box>
                </SimpleModal>
              )}
            </>
          )}
        </Box>
      </Box>
    );
  };

  if (attributes) {
    return (
      <StyledRaisedContainer
        background={page.cardBg}
        justify="center"
        align="start"
        gap="0.75rem"
        pad="1rem"
        width={small ? '100%' : 'fit-content'}
        height={(details || []).length ? `${details.length * 2}rem` : '6rem'}
        style={{ minWidth: small ? '0' : '12rem', flex: 1 }}
      >
        {details.map(({
          label, value, formatValue, type, unit,
        }) => (
          renderDetails(label, formatValue, value, type, unit)
        ))}
      </StyledRaisedContainer>
    );
  }

  return (
    <Box direction="column" gap="0.5rem">
      {details.map(({
        label, value, formatValue, type, unit,
      }) => (
        renderDetails(label, formatValue, value, type, unit)
      ))}
    </Box>
  );
};

SearchDetails.propTypes = {
  small: PropTypes.bool.isRequired,
  details: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string.isRequired),
    ]).isRequired,
    formatValue: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
    ]),
    type: PropTypes.string.isRequired,
    unit: PropTypes.string,
  })).isRequired,
  statusColorMap: PropTypes.shape({
    running: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
    completed: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
    paused: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
    scheduled: PropTypes.shape({
      text: PropTypes.string.isRequired,
      background: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  textColor: PropTypes.string.isRequired,
  highlightText: PropTypes.string,
  weight: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  attributes: PropTypes.bool,
};

const SimpleCardWidget = ({
  small,
  textColor,
  label,
  value,
  unit = null,
  boxMargin = '0',
  backgroundColor = 'var(--page-card-bg)',
}) => {
  const displayValue = unit ? `${value}${unit}` : value;

  return (
    <StyledRaisedContainer
      background={backgroundColor}
      height={{ min: '6rem' }}
      width="12rem"
      justify="between"
      align="start"
      gap="0.75rem"
      pad="1rem"
      margin={boxMargin}
    >
      <StyledText size="1rem" color={textColor} weight={600}>{label}</StyledText>
      <StyledText size={small ? '1.5rem' : '2rem'} color={textColor} weight={800}>{displayValue}</StyledText>
    </StyledRaisedContainer>
  );
};

SimpleCardWidget.propTypes = {
  small: PropTypes.bool.isRequired,
  textColor: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  unit: PropTypes.string,
  boxMargin: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.objectOf(PropTypes.any.isRequired),
  ]),
  backgroundColor: PropTypes.string,
};

const TableControls = ({
  small,
  displayInputValue = null,
  searchQuery,
  handleFilter = () => null,
  handleSort = () => null,
  filterSortMeta = null,
  searchQueryKey = null,
}) => {
  if (filterSortMeta) {
    const useFilterOptions = _.filter(
      filterSortMeta.filters.options,
      (dt) => dt && (!dt?.validPredicates?.includes('i_cont') && !dt?.validPredicates?.includes('in')),
    );

    // Extract sort field and direction from searchQuery
    const sortField = searchQuery?.sort?.name || null;
    const sortDirection = searchQuery?.sort?.direction || 'desc';

    return (
      <Box wrap direction="row" gap="0.5rem" align="center">
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <SearchInput
            key="search"
            small={small}
            handleSearch={(uid, value, predicate, seperateValues, reset) => (
              handleFilter(uid, value, predicate, seperateValues, reset)
            )}
            initialValue={displayInputValue}
            queryKey={searchQueryKey}
          />
        </Box>
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <FilterDropdown
            key={uniqid()}
            small={small}
            selectedFilters={searchQuery.filters || null}
            handleFilter={(uid, value, predicate, seperateValues, reset) => (
              handleFilter(uid, value, predicate, seperateValues, reset)
            )}
            filterOptions={useFilterOptions}
          />
        </Box>
        <Box pad={{ vertical: small ? '0.5rem' : '0' }}>
          <DropdownSort
            sortOptions={filterSortMeta.sort.name.options.map((opt) => ({
              api: opt.name,
              display: opt.formatted,
            }))}
            customPad="0.35rem 0.3rem"
            selectedSortField={sortField}
            sortDirection={sortDirection}
            onSortFieldChange={(field, direction) => handleSort(field, direction)}
            onSortDirectionToggle={() => {
              // Toggle direction and call handleSort with current field
              const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
              handleSort(sortField, newDirection);
            }}
          />
        </Box>
      </Box>
    );
  }

  return null;
};

TableControls.propTypes = {
  small: PropTypes.bool.isRequired,
  displayInputValue: PropTypes.string,
  searchQuery: PropTypes.shape({
    paging: PropTypes.shape({
      to: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
    }),
    sort: PropTypes.shape({
      name: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
    }),
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any,
      predicate: PropTypes.string.isRequired,
    })),
  }).isRequired,
  handleFilter: PropTypes.func,
  handleSort: PropTypes.func,
  filterSortMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validValues: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired),
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      })).isRequired,
    }).isRequired,
  }),
  searchQueryKey: PropTypes.string,
};

export const WidgetRenderer = ({
  small,
  size,
  loading,
  noDataMessage = null,
  widgetData = null,
  padding = '0',
  initializeReviewedRows,
  updateRowReviewStatus,
  updateMultipleRowsReviewStatus,
  bulkUpdateReviewStatusByDomain = null,
  selectedStatuses = null,
  resultsPerPage = null,
  resultsPerPageOptions,
  currentPage = null,
  totalResults = null,
  handleQuery,
  handleFilter = () => null,
  handleSort = () => null,
  handleExportCSV = null,
  searchQuery,
  filterSortMeta,
  hideValueWidgets = false,
  searchDetails = null,
  paymentPending = false,
  watermarkGroupingKey = null,
  onSelectionChange = null,
  hostingData = null,
  includeISPData = false,
}) => {
  const { theme: { page } } = useAppTheme();
  const valueWidgets = _.filter(widgetData, ((dt) => dt.type === 'value'));
  const reviewWidget = _.find(widgetData, ((dt) => dt.type === 'review-table'));
  const reviewHeaders = small ? _.filter(reviewWidget?.value?.header, ((dt) => dt.display !== 'Review Status')) : reviewWidget?.value?.header;
  const useSearchQueryKey = filterSortMeta?.filters?.options && _.find(filterSortMeta.filters.options, ((dt) => dt.validPredicates.includes('i_cont')));
  const searchInputValue = searchQuery?.filters && _.find(searchQuery.filters, ['name', filterSortMeta?.searchQueryKey ?? useSearchQueryKey?.name]);
  let displayInputValue = '';

  if (searchInputValue && searchInputValue.value) {
    displayInputValue = searchInputValue.value;
  }

  if (loading || !widgetData) {
    return (
      <Box flex justify="center" align="center">
        <Spinning size="large" color={page.highlight} />
      </Box>
    );
  }

  return (
    <Box direction="column" gap={padding}>
      {!hideValueWidgets && valueWidgets?.length >= 1 && (
        <Box wrap={!small} direction={small ? 'column' : 'row'} gap="1.5rem" flex="1" pad={{ horizontal: padding }}>
          {searchDetails && (
            <Box flex="1">
              {searchDetails}
            </Box>
          )}
          <Box direction="row" gap="1.5rem" flex="1">
            {valueWidgets.map(({ label, value }) => (
              <SimpleCardWidget
                key={uniqid()}
                small={small}
                backgroundColor={page.cardBg}
                textColor={page.primaryText}
                label={label}
                value={value}
              />
            ))}
          </Box>
        </Box>
      )}
      <Box>
        {reviewWidget && (
          <ReviewTablePartial
            loading={loading}
            reviewData={reviewWidget?.value?.rows}
            reviewDataHeaders={reviewHeaders}
            reviewStatusOptions={reviewWidget?.statusOptions}
            initializeReviewedRows={initializeReviewedRows}
            updateRowReviewStatus={updateRowReviewStatus}
            updateMultipleRowsReviewStatus={updateMultipleRowsReviewStatus}
            bulkUpdateReviewStatusByDomain={bulkUpdateReviewStatusByDomain}
            selectedStatuses={selectedStatuses}
            handleQuery={handleQuery}
            handleResultsPerPage={(val) => handleQuery('paging', 'size', val)}
            handlePage={(val) => handleQuery('paging', 'to', val)}
            handleExportCSV={handleExportCSV}
            resultsPerPageOptions={resultsPerPageOptions}
            resultsPerPage={resultsPerPage}
            currentPage={currentPage}
            totalResults={totalResults}
            size={size}
            searchQuery={searchQuery}
            handleFilter={handleFilter}
            customHeader={reviewWidget?.label}
            noDataMessage={noDataMessage}
            paymentPending={paymentPending}
            watermarkGroupingKey={watermarkGroupingKey}
            onSelectionChange={onSelectionChange}
            hostingData={hostingData}
            includeISPData={includeISPData}
            tableControls={(
              <TableControls
                small={small}
                displayInputValue={displayInputValue}
                searchQuery={searchQuery}
                handleFilter={handleFilter}
                handleSort={handleSort}
                filterSortMeta={filterSortMeta}
                searchQueryKey={filterSortMeta?.searchQueryKey ?? useSearchQueryKey?.name}
              />
            )}
          />
        )}
      </Box>
    </Box>
  );
};

/* eslint-disable camelcase */
WidgetRenderer.propTypes = {
  small: PropTypes.bool.isRequired,
  size: PropTypes.oneOf(['xsmall', 'small', 'medium', 'mediumlarge', 'large']).isRequired,
  loading: PropTypes.bool.isRequired,
  noDataMessage: PropTypes.string,
  widgetData: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
    type: PropTypes.string.isRequired,
    unit: PropTypes.string,
    feature: PropTypes.string,
    statusOptions: PropTypes.arrayOf(PropTypes.shape({
      api: PropTypes.string.isRequired,
      display: PropTypes.string.isRequired,
    })),
  }).isRequired),
  padding: PropTypes.string,
  selectedStatuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    review_status: PropTypes.string.isRequired,
  })),
  resultsPerPageOptions: PropTypes.arrayOf(PropTypes.number.isRequired).isRequired,
  resultsPerPage: PropTypes.number,
  currentPage: PropTypes.number,
  handleQuery: PropTypes.func.isRequired,
  handleFilter: PropTypes.func,
  handleSort: PropTypes.func,
  initializeReviewedRows: PropTypes.func.isRequired,
  updateRowReviewStatus: PropTypes.func.isRequired,
  updateMultipleRowsReviewStatus: PropTypes.func.isRequired,
  bulkUpdateReviewStatusByDomain: PropTypes.func,
  handleExportCSV: PropTypes.func,
  totalResults: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  searchQuery: PropTypes.shape({
    paging: PropTypes.shape({
      to: PropTypes.number.isRequired,
      size: PropTypes.number.isRequired,
    }),
    sort: PropTypes.shape({
      name: PropTypes.string.isRequired,
      direction: PropTypes.string.isRequired,
    }),
    filters: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.any,
      predicate: PropTypes.string.isRequired,
    })),
  }).isRequired,
  filterSortMeta: PropTypes.shape({
    sort: PropTypes.shape({
      name: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
      direction: PropTypes.shape({
        options: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired).isRequired,
      }).isRequired,
    }).isRequired,
    filters: PropTypes.shape({
      options: PropTypes.arrayOf(PropTypes.shape({
        name: PropTypes.string.isRequired,
        formatted: PropTypes.string.isRequired,
        validValues: PropTypes.arrayOf(PropTypes.shape({
          name: PropTypes.string.isRequired,
          formatted: PropTypes.string.isRequired,
        }).isRequired),
        validPredicates: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
      })).isRequired,
    }).isRequired,
    searchQueryKey: PropTypes.string,
  }),
  hideValueWidgets: PropTypes.bool,
  searchDetails: PropTypes.node,
  paymentPending: PropTypes.bool.isRequired,
  watermarkGroupingKey: PropTypes.string,
  onSelectionChange: PropTypes.func,
  hostingData: PropTypes.arrayOf(PropTypes.shape({ host: PropTypes.string })),
  includeISPData: PropTypes.bool,
};
/* eslint-enable camelcase */
